import { Radio, Table, TableProps, Tag, Typography } from 'antd';
import { SorterResult, SortOrder } from 'antd/es/table/interface';
import { createSearchParams, Link, To, useSearchParams } from 'react-router-dom';

import { SearchCandidate } from '../../hooks/useSearchCandidates';
import {
  InterviewDetail,
  SankhyaAdmissionCollection,
  VixtingAdmissionCollection,
} from '../../types';
import { CandidateTagSerializer } from '../../types/api';
import Filter from '../Filter';
import CandidateIntegrationAdmissionSent from './CandidateIntegrationAdmissionSent';
import styles from './CandidateTable.module.css';
import RecruitmentProcessStatus from './RecruitmentProcessStatus';
import TestResultClassification from './TestResultClassification';
import TestResultScore from './TestResultScore';

interface TableSearchCandidate extends SearchCandidate {
  opinion_status?: InterviewDetail['opinion_status'];
}

interface CandidateTableProps extends TableProps<TableSearchCandidate> {
  showIntegrationAdmissionStatus?: boolean;
  integrationAdmissions?: (SankhyaAdmissionCollection | VixtingAdmissionCollection | null)[];
  onRowClick?: (recordId: SearchCandidate['id']) => To;
  candidateTags?: CandidateTagSerializer[];
  opinionStatusColumnVisible?: boolean;
  showIntegrationAdmissionLogs?: boolean;
}

const CandidateTable = ({
  showIntegrationAdmissionStatus = false,
  showIntegrationAdmissionLogs = false,
  integrationAdmissions = [],
  onRowClick,
  pagination,
  children,
  candidateTags,
  opinionStatusColumnVisible,
  ...props
}: CandidateTableProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  return (
    <Table
      data-testid="candidate-table"
      rowKey="id"
      pagination={{
        showTotal: (total) => {
          return `Total: ${total} candidatos`;
        },
        onChange: (page, pageSize) => {
          searchParams.set('page', page.toString());
          searchParams.set('pageSize', pageSize.toString());
          setSearchParams(searchParams);
        },
        current: parseInt(searchParams.get('page') || '1', 10),
        pageSize: parseInt(searchParams.get('pageSize') || '10', 10),
        ...(pagination ?? {}),
      }}
      rowClassName={(record, index) =>
        [
          styles.tableRow,
          ...(record.recruiter_visualization_id ? [styles.tableRowOpacity] : []),
        ].join(' ')
      }
      onChange={(pagination, filters, sorter) => {
        const sort = sorter as SorterResult<TableSearchCandidate>;

        searchParams.set('sort', (sort.columnKey as string) ?? '');
        searchParams.set('direction', sort.order ?? '');
        setSearchParams(searchParams);
      }}
      sortDirections={['descend', 'ascend']} // Setting available sort directions and the order in which the sort occurs: no sort --> descend --> ascend --> no sort
      {...props}
    >
      <Table.Column
        key="name"
        title={<Typography.Text data-testid="candidate-name-column">Nome</Typography.Text>}
        sortOrder={
          searchParams.get('sort') === 'name' ? (searchParams.get('direction') as SortOrder) : null
        }
        sorter
        dataIndex="name"
        render={(value, record: TableSearchCandidate, index) => (
          <>
            <Link
              className="ant-typography-link"
              to={
                onRowClick
                  ? onRowClick(record.id)
                  : {
                      pathname: `candidates/${record.id}/details`,
                      search: createSearchParams(searchParams).toString(),
                    }
              }
              style={{
                marginRight: '3%',
                ...(record.recruiter_visualization_id ? {} : { fontWeight: 'bold' }),
              }}
              data-testid={`candidate-name-${record.id}`}
            >
              {value}
            </Link>
            {showIntegrationAdmissionStatus &&
              (showIntegrationAdmissionLogs ? (
                <Link
                  to={
                    onRowClick
                      ? onRowClick(record.id)
                      : {
                          pathname: integrationAdmissions[index]?.id
                            ? `vixting_admission/${integrationAdmissions[index]?.id}/logs`
                            : '',
                          search: createSearchParams(searchParams).toString(),
                        }
                  }
                >
                  <CandidateIntegrationAdmissionSent
                    integrationAdmissionStatus={integrationAdmissions[index]?.status}
                  />
                </Link>
              ) : (
                <CandidateIntegrationAdmissionSent
                  integrationAdmissionStatus={integrationAdmissions[index]?.status}
                />
              ))}
            {record.recruitmentProcess && (
              <>
                <br />
                <RecruitmentProcessStatus
                  status={record.recruitmentProcess.status}
                  data-testid={`candidate-status-${record.id}`}
                />
              </>
            )}
          </>
        )}
      />
      {(!searchParams.has('columns') || searchParams.getAll('columns').includes('TAGS')) && (
        <Table.Column
          key="name"
          title="Tags"
          align="center"
          dataIndex="tags"
          render={(value, record: TableSearchCandidate) => (
            <>
              {record.tags.slice(0, 3).map((id) => (
                <Tag key={id}>
                  {candidateTags?.find((candidateTag) => candidateTag.id === id)?.name || ''}
                </Tag>
              ))}
              {record.tags.length > 3 && <Tag>...</Tag>}
              {record.tags.length === 0 && <Typography.Text>-</Typography.Text>}
            </>
          )}
        />
      )}
      {opinionStatusColumnVisible && searchParams.getAll('columns').includes('OPINION_STATUS') && (
        <Table.Column
          key="opinionStatus"
          align="center"
          title="Parecer"
          className={styles.scoreCell}
          dataIndex="opinion_status"
          render={(value, record: TableSearchCandidate) => (
            <>
              {record.opinion_status === 'PENDING' && <Tag>Pendente</Tag>}
              {record.opinion_status === 'IN_ANALYSIS' && <Tag color="warning">Em análise</Tag>}
              {record.opinion_status === 'QUALIFIED' && <Tag color="blue">Qualificado</Tag>}
              {record.opinion_status === 'NOT_QUALIFIED' && <Tag color="red">Não qualificado</Tag>}
            </>
          )}
        />
      )}
      {(!searchParams.has('columns') || searchParams.getAll('columns').includes('MATCH')) && (
        <Table.Column
          key="match"
          align="center"
          title="Match"
          filterDropdown={
            <Filter.Dropdown>
              <Filter.Range nameMin="match_min" nameMax="match_max" />
            </Filter.Dropdown>
          }
          sortOrder={
            searchParams.get('sort') === 'match'
              ? (searchParams.get('direction') as SortOrder)
              : null
          }
          sorter
          className={styles.scoreCell}
          dataIndex="match"
          render={(value, record: SearchCandidate) => (
            <TestResultScore data-testid={`match-test-result-score-${record.id}`} score={value} />
          )}
        />
      )}
      {(!searchParams.has('columns') ||
        searchParams.getAll('columns').includes('RAW_POTENTIAL')) && (
        <Table.Column
          key="raw_potential"
          align="center"
          title="Potencial&nbsp;Bruto"
          filterDropdown={
            <Filter.Dropdown>
              <Filter.Range nameMin="raw_potential_min" nameMax="raw_potential_max" />
            </Filter.Dropdown>
          }
          sortOrder={
            searchParams.get('sort') === 'raw_potential'
              ? (searchParams.get('direction') as SortOrder)
              : null
          }
          sorter
          className={styles.scoreCell}
          dataIndex="raw_potential"
          render={(value, record: SearchCandidate) => (
            <TestResultScore
              data-testid={`raw-potential-test-result-score-${record.id}`}
              score={value}
            />
          )}
        />
      )}
      {(!searchParams.has('columns') || searchParams.getAll('columns').includes('OCP')) && (
        <Table.Column
          key="ocp"
          align="center"
          title="Fit Cultural"
          filterDropdown={
            <Filter.Dropdown>
              <Filter.Range nameMin="ocp_min" nameMax="ocp_max" />
            </Filter.Dropdown>
          }
          sortOrder={
            searchParams.get('sort') === 'ocp' ? (searchParams.get('direction') as SortOrder) : null
          }
          sorter
          className={styles.scoreCell}
          dataIndex="ocp"
          render={(value, record: SearchCandidate) => (
            <TestResultScore data-testid={`ocp-test-result-score-${record.id}`} score={value} />
          )}
        />
      )}
      {(!searchParams.has('columns') || searchParams.getAll('columns').includes('LOGIC')) && (
        <Table.Column
          align="center"
          title="Mental"
          key="logic"
          filterDropdown={
            <Filter.Dropdown>
              <Filter.Range nameMin="logic_min" nameMax="logic_max" />
            </Filter.Dropdown>
          }
          sortOrder={
            searchParams.get('sort') === 'logic'
              ? (searchParams.get('direction') as SortOrder)
              : null
          }
          sorter
          className={styles.scoreCell}
          dataIndex="logic"
          render={(value, record: SearchCandidate) => (
            <TestResultScore data-testid={`logic-test-result-score-${record.id}`} score={value} />
          )}
        />
      )}
      {(!searchParams.has('columns') || searchParams.getAll('columns').includes('SJT')) && (
        <Table.Column
          align="center"
          title="Social"
          key="sjt"
          filterDropdown={
            <Filter.Dropdown>
              <Filter.Range nameMin="sjt_min" nameMax="sjt_max" />
            </Filter.Dropdown>
          }
          sortOrder={
            searchParams.get('sort') === 'sjt' ? (searchParams.get('direction') as SortOrder) : null
          }
          sorter
          className={styles.scoreCell}
          dataIndex="sjt"
          render={(value, record: SearchCandidate) => (
            <TestResultScore data-testid={`sjt-test-result-score-${record.id}`} score={value} />
          )}
        />
      )}
      {(!searchParams.has('columns') ||
        searchParams.getAll('columns').includes('MOTIVATIONAL')) && (
        <Table.Column
          key="motivational"
          align="center"
          title="Motivacional"
          filterDropdown={
            <Filter.Dropdown>
              <Filter.Range nameMin="motivational_min" nameMax="motivational_max" />
            </Filter.Dropdown>
          }
          sortOrder={
            searchParams.get('sort') === 'motivational'
              ? (searchParams.get('direction') as SortOrder)
              : null
          }
          sorter
          className={styles.scoreCell}
          dataIndex="motivational"
          render={(value, record: SearchCandidate) => (
            <TestResultScore
              data-testid={`motivational-test-result-score-${record.id}`}
              score={value}
            />
          )}
        />
      )}
      {(!searchParams.has('columns') || searchParams.getAll('columns').includes('OPERATIONAL')) && (
        <Table.Column
          key="operational"
          align="center"
          title="Raciocínio Simplificado"
          filterDropdown={
            <Filter.Dropdown>
              <Filter.RadioGroup name="operational_logic">
                <Radio value="Aprovado">Aprovado</Radio>
                <Radio value="Reprovado">Reprovado</Radio>
              </Filter.RadioGroup>
            </Filter.Dropdown>
          }
          sortOrder={
            searchParams.get('sort') === 'operational'
              ? (searchParams.get('direction') as SortOrder)
              : null
          }
          sorter
          className={styles.scoreCell}
          dataIndex="operational"
          render={(value, record: SearchCandidate) => (
            <TestResultClassification
              data-testid={`operational-test-result-classification-${record.id}`}
              score={value}
            />
          )}
        />
      )}
      {children}
    </Table>
  );
};

export default CandidateTable;
